import { camelCase, memoize, startCase } from 'lodash';
import { green, red, yellow } from '@mui/material/colors';
import moment from 'moment';

const timelineChoices = {
  allocate_investment: 'Allocate investment',
  submit_for_feedback: 'Submit for feedback',
  resolve_feedback: 'Resolve feedback',
  publish_report: 'Publish report',
  finalize_report: 'Finalize report'
};

const taskTypes = {
  1: 'allocate',
  2: 'submit',
  3: 'resolve',
  4: 'publish',
  6: 'finalize',
};

const evalTasksToGetProgress = (tasks) => {
  if (!tasks) {
    return {
      color: green[500],
      value: 0,
      name: 'no tasks'
    };
  }
  const status = tasks?.[0]?.status;
  const totalTask = tasks.length;
  const completedTask = tasks.filter(task => task.status === 'completed').length;
  let color = green[500];
  let value = (totalTask > 0 ) ? Math.round((completedTask / totalTask) * 100) : 0;
  if (totalTask === 1 && completedTask === 0) {
    value = 50;
  }
  switch (status) {
    case 'overdue':
      color = red[500];
    // falls through
    case 'completed':
      color = yellow[500];
    // falls through
    default:
      color = green[500];
  }
  return { 
    color,
    value,
    name: (status) ? `${value}% ${status}` : 'no tasks'
  };
};

const getVariation = (perc) => {
  if (perc > 66) {
    return 'positive';
  }
  if (perc < 0) {
    return 'negative';
  }
  return 'inactive';
};

const taskDistribution = memoize((tasks) => {
  const total = tasks.length;
  const completed = tasks.filter(task => task.status === 'completed').length;
  const due = tasks.filter(task => task.status === 'uncompleted').length;
  const overdue = tasks.filter(task => task.status === 'overdue').length;
  const completedPerc = (total > 0) ? Math.round((completed/total)*100) : 0;
  const duePerc = (total > 0) ? Math.round((due/total)*100) : 0;
  const overduePerc = (total > 0) ? Math.round((overdue/total)*100) : 0;
  
  return { due, overdue };
}, (args) => JSON.stringify(args));

const timelinesTask = memoize((fields) => {
  const tasks = (fields.tasks||[]);
  const total = tasks.length;
  // The colors of each box will correspond to the percentage complete, in buckets of less than 33%, 33-66%, and greater than 66%.
  function getVariation(value) {
    if (value === 0) {
      return 'inactive';
    } else if (value > 0 && value < 33) {
      return 'negative';
    } else if (value >= 33 && value <= 66) {
      return 'neutral';
    } else {
      return 'positive';
    }
  }
  // percentage of tasks associated to the reports in the portfolio that are due or completed
  function getPercent(day) {
    if (total === 0) return 0;
    const type = taskTypes[day];
    const completed = tasks.filter(t => (t.taskType === type && t.status === 'completed')).length;
    return ((completed / total) * 100).toFixed(2);
  }
  function getTaskDetails(day) {
    const type = taskTypes[day];
    return [
      `${getPercent(day)}%`,
      `Complete - ${tasks.filter(t => (t.taskType === type && t.status === 'completed')).length}`,
      `Due - ${tasks.filter(t => (t.taskType === type && t.status === 'uncompleted')).length}`,
      `Overdue - ${tasks.filter(t => (t.taskType === type && t.status === 'overdue')).length}`,
    ];
  }
  function getDate(day) {
    const date = fields[`reportDate${day}`];
    const from = fields[`timelineFrom${day}`];
    const days = fields[`timelineDays${day}`];
    return date ? moment(date).format('MMM D') : (from && days) ? `${timelineChoices[from]} + days` : 'N/A';
  }
  const now = moment();
  function getDays(step) {
    const date = fields[`reportDate${step}`];
    if (!date) {
      return null;
    }

    return 0;
  }
  return [
    {
      days: getDays(1),
      date: getDate(1),
      text: getDate(1),
      label: 'Allocate',
      variation: getVariation(getPercent(1)),
      details: getTaskDetails(1),
    },
    { days: getDays(2), text: getDate(2), label: 'Submit', variation: getVariation(getPercent(2)), details: getTaskDetails(2)},
    { days: getDays(3), text: getDate(3), label: 'Resolve', variation: getVariation(getPercent(3)), details: getTaskDetails(3)},
    { days: getDays(4), text: getDate(4), label: 'Publish', variation: getVariation(getPercent(4)), details: getTaskDetails(4)},
    { days: getDays(6), text: getDate(6), label: 'Finalize', variation: getVariation(getPercent(6)), details: getTaskDetails(6)},
  ];
}, (args) => args?.id);

const getRows = memoize((query) => {
  return query.reduce((allP, { investments, ...portfolio }) => {
    if (!investments || investments.length === 0) {
      return [
        ...allP,
        {
          ...portfolio,
          grantee: {
            name: 'Not yet allocated'
          }
        }
      ];
    }
    return [
      ...allP,
      ...investments
        .filter(({ enabled = true }) => enabled)
        .reduce((allI, { allocations, ...investment }) => [
          ...allI,
          ...(allocations.length
            ? allocations
                .reduce((allA, allocation) => [
                  ...allA,
                  {
                    ...portfolio,
                    portfolioId: portfolio.id,
                    name: portfolio.name ?? `(#${portfolio.id})`,
                    portfolioStatus: titleCase(portfolio.status),
                    ...allocation,
                    investmentId: investment.id,
                    allocationId: allocation.id,
                    ...investment,
                    relationshipManagerName: [
                      portfolio?.managerFirstName,
                      portfolio?.managerLastName
                    ]
                      .filter(part => part)
                      .join('  '),
                    reportStage: mapStage(allocation?.report?.stage) ?? 'Unallocated',
                    primaryInterventionType: getIntenventionType(allocation?.report),
                    combinedStatus: fixCombinedStatus(allocation?.report?.status || investment?.status),
                    amountCategory5: getAmountCategory5(allocation?.amount),
                    amountCategory10: getAmountCategory10(allocation?.amount),
                    amountCategory100: getAmountCategory100(allocation?.amount),
                    amountCategory500: getAmountCategory500(allocation?.amount),
                    totalInvestments: investments?.length,
                    totalReports: allocations.filter(({ report }) => Boolean(report)).length,
                    allocationUpdatedAt: allocation?.updatedAt,
                    allocationCreatedAt: allocation?.createdAt,
                    invitationUpdatedAt: investment?.invitations?.[0]?.updatedAt,
                    invitationCreatedAt: investment?.invitations?.[0]?.createdAt,
                    invitationDt: investment?.invitations?.[investment?.invitations.length - 1]?.notificationDt,
                  }
                ], [])
              : [{
                ...portfolio,
                portfolioId: portfolio.id,
                name: portfolio.name ?? `(#${portfolio.id})`,
                portfolioStatus: titleCase(portfolio.status),
                ...investment,
                grantee: {
                  name: investment.organization
                    ?? investment.invitations?.[0]?.organization,
                },
                reportStage: 'Unallocated',
                relationshipManagerId: investment?.managerId ?? portfolio?.managerId,
                relationshipManager: {
                  firstName: portfolio?.managerFirstName,
                  lastName: portfolio?.managerLastName,
                  email: portfolio?.managerEmail
                },
                relationshipManagerName: [
                  portfolio?.managerFirstName,
                  portfolio?.managerLastName,
                ]
                  .filter(part => part)
                  .join(' '),
                investmentId: investment.id,
                combinedStatus: fixCombinedStatus(investment?.status),
                amountCategory: 'UNALLOCATED',
                totalInvestments: investments?.length,
                totalReports: 0,
                allocationUpdatedAt: null,
                allocationCreatedAt: null,
                invitationUpdatedAt: investment?.invitations?.[0]?.updatedAt,
                invitationCreatedAt: investment?.invitations?.[0]?.createdAt,
                invitationDt: investment?.invitations?.[investment?.invitations.length - 1]?.notificationDt,
                amount: (investment?.cashAmount ?? 0) +
                  (investment?.servicesAmount ?? 0) +
                  (investment?.goodsAmount ?? 0) +
                  (investment?.volunteerismAmount),
              }])
        ], [])
    ];
  }, []);
});

const resolveClaim = (row) => {
  if (row?.tier && row?.tier === 'FOUNDATIONAL' && row?.report && row?.amount) {
    const report = row.report;
    const total = (report.cost_current_direct_cash || 0) +
      (report.cost_current_direct_in_kind || 0) +
      (report.cost_current_indirect_cash || 0) +
      (report.cost_current_indirect_in_kind || 0);
    if (total) return row.amount / total;
  }
  return 0;
};

const resolveTotalBudgetInitial = (row) => {
  if (row?.report) {
    return (row.report.cost_initial_direct_cash || 0) +
      (row.report.cost_initial_direct_in_kind || 0) +
      (row.report.cost_initial_indirect_cash || 0) +
      (row.report.cost_initial_indirect_in_kind || 0);
  }
  return 0;
};

const resolveTotalReached = (row, totalBudgetInitial) => {
    if (totalBudgetInitial && row?.amount && row?.report && row?.report?.results && row?.report?.results.length > 0) {
      const amount_budget = (totalBudgetInitial > 0) ? (row.amount / totalBudgetInitial) : 0;
      const claim_percentage =  (amount_budget <= 1) ? amount_budget : 1;
      const forecast = row.report.results.reduce((acc, result) => { return (acc + result.forecastCurrent); }, 0);
      return amount_budget * forecast;
    }
  return 0;
};

const claimedReach = (row) => {
  if (row?.report && row?.report?.results && row?.report?.results.length > 0) {
    return row?.report?.results[0].claim_percentage ?? 0;
  }
  return 0;
};

const getAmountCategory5 = (amount) => {
  if (!amount) {
    return 'Unallocated';
  }
  if (amount < 5000) {
    return 'Below  $5K';
  }
  if (amount < 25000) {
    return 'Below $25K';
  }
  if (amount < 100000) {
    return 'Below $100K';
  }
  return 'Above $100K';
};

const getAmountCategory10 = (amount) => {
  if (!amount) {
    return 'Unallocated';
  }
  if (amount < 10000) {
    return 'Below $10K';
  }
  if (amount < 50000) {
    return 'Below $50K';
  }
  if (amount < 100000) {
    return 'Below $100K';
  }
  return 'Above $100K';
};

const getAmountCategory100 = (amount) => {
  if (!amount) {
    return 'Unallocated';
  }
  return `Below $${Math.ceil(amount / 100000) * 100}K`;
};

const getAmountCategory500 = (amount) => {
  if (!amount) {
    return 'Unallocated';
  }
  return `Below $${Math.ceil(amount / 500000) * 500}K`;
};

const titleCase = str => startCase(camelCase(str));

const fixCombinedStatus = str => str === 'ALLOCATED'
  ? ''
  : titleCase(str)
    .replace(/Ti /g, 'TI ');

const keyMappers = {
  stage: {
    INITIAL: 'Initial Report',
    DRAFT: 'Draft',
    INTERIM: 'Interim Report',
    FINAL: 'Final Report'
  }
};

const makeKeyMapper = mapper => x => mapper[x] ?? x;
const keyMapperForField = field => makeKeyMapper(keyMappers[field]);
const mapStage = keyMapperForField('stage');
  
const getIntenventionType = (report) => {
  if (!report) {
    return null;
  }
  const { primaryInterventionType } = report;
  if (!primaryInterventionType) {
    return null;
  }
  if (typeof primaryInterventionType === 'string' && !primaryInterventionType.trim().startsWith('{')) {
    return primaryInterventionType;
  }
  try {
    const { values } = JSON.parse(primaryInterventionType);
    return values?.label ?? values?.name ?? null;
  } catch {
    return null;
  }
};

export const serialize = (query) => {
  const rows = getRows(query);
  const data = query && rows.map(row => {
    const claim = resolveClaim(row);
    const totalBudgetInitial = resolveTotalBudgetInitial(row);
    const totalReached = claimedReach(row);
    return {
      ...row,
      claim,
      totalReached,
      totalBudgetInitial,
      programName: row.program?.name
        ? row.program.name
        : row.report?.name 
          ?? row.programNameByDonor
          ?? row.invitations?.[0]?.organization
          ?? '-',
      programManager: {
        firstName: row.program?.firstName,
         lastName: row.program?.lastName,
         email: row.program?.email,
      },
      investmentTotal: (row.cashAmount ?? 0 + row.goodsAmount ?? 0 + row.servicesAmount ?? 0 + row.volunteerismAmount ?? 0),
      progress: evalTasksToGetProgress(row.tasks),
      taskDistribution: taskDistribution(row.tasks||[]),
      timelinesTask: timelinesTask(row),
      overdueTask: row.tasks?.find(({ status }) => status === 'overdue'),
      allocationAmount: row.amount ?? 0,
      claimPercentage: (claim) ? `${Math.round(parseFloat(claim) * 100)}%` : '0%',
      impacts: row.report?.results?.map(({ indicator }) => indicator)?.join(' | ') ?? '',
      logoFileUrl: row.report?.program?.logo
          ?? row.report?.logo
          ?? row?.granteeLogo
          ?? row?.funderLogo,
      grantee: {
        name: row.granteeName
      }
    };
  });
  return data;
};